// Requests on a phone, after SPhoneRequests: back to More, the title, Waiting or All, one row per card (where it came
// from, when, where it goes, its state) opening a sheet with what came in, what the assistant will do, and Approve
// and Ignore (Undo after an ignore). Context suggestions are a violet group per space below, optional and uncounted.
import { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { useQueryClient } from '@tanstack/react-query';
import { EmailOrigin, isSuggestion, PLACE_FIRST, type ProposedAction } from '@dpw/shared';
import { api } from '../api';
import { useActions, useMe } from '../lib/queries';
import { useDecide } from '../lib/decide';
import { useLinger } from '../lib/linger';
import { spring } from '../lib/motion';
import { NOT_IGNORABLE, STATUS_WORD, ApproveCard } from '../ui/ApproveCard';
import { SkelList } from '../ui/motion';
import { useToast } from '../ui/toast';
import { EmailExtras, EmailTarget, ICON, classify, isRetargetable, recordingMinutes, tone, whenOf } from '../pages/Requests';
import { MinutesBody } from '../meetings/parts';
import { P, PSheet, Seg, Tag, Title, TopBar } from './kit';
import { ForwardHelp, PlacePicker } from '../ui/email';

const TAG = { waiting: 'a', approved: 'g', ignored: '', rejected: '' } as const;
const IC = { grn: 'grn', neu: '', red: 'red', amb: 'amb', acc: 'acc' } as const;

/** What came in: the email (subject, then the body), else where it came from. */
function cameIn(a: ProposedAction) {
  const e = EmailOrigin.safeParse(a.origin);
  if (e.success && (e.data.subject || e.data.body)) return [e.data.subject, e.data.body].filter(Boolean).join('\n\n');
  const from = typeof a.origin.from === 'string' ? a.origin.from : null;
  return `${a.sourceLabel}${from ? `, from ${from}` : ''}.`;
}

export function PhoneRequests() {
  const [status, setStatus] = useState<'waiting' | 'all'>('waiting');
  const q = useActions(status);
  const waitingQ = useActions('waiting');
  const [params, setParams] = useSearchParams();
  const [sel, setSel] = useState<string | null>(params.get('id'));
  const data = useMemo(() => q.data ?? [], [q.data]);
  const rows0 = useMemo(() => data.filter(a => !(isSuggestion(a) && a.status === 'waiting')), [data]);
  const rows = useLinger(rows0, a => status === 'waiting' && a.status !== 'waiting');
  const groups = useMemo(() => {
    const m = new Map<string, { spaceId: string; spaceName: string; cards: ProposedAction[] }>();
    for (const a of data) {
      if (!isSuggestion(a) || a.status !== 'waiting') continue;
      const k = a.spaceId ?? 'none';
      if (!m.has(k)) m.set(k, { spaceId: k, spaceName: a.spaceName ?? 'No space', cards: [] });
      m.get(k)!.cards.push(a);
    }
    return [...m.values()].sort((x, y) => x.spaceName.localeCompare(y.spaceName));
  }, [data]);
  const waiting = (waitingQ.data ?? []).filter(a => !isSuggestion(a)).length;
  // The open card stays (with its result) after it leaves the list.
  const last = useRef<ProposedAction | null>(null);
  const found = data.find(a => a.id === sel) ?? (waitingQ.data ?? []).find(a => a.id === sel);
  if (found) last.current = found;
  const cur = found ?? (last.current?.id === sel ? last.current : null);
  const open = (id: string | null) => { setSel(id); setParams(id ? { id } : {}, { replace: true }); };

  return (
    <div className="ph-page">
      <TopBar back={{ to: '/more', label: 'More' }} />
      <Title title="Requests" sub="Things the assistant wants to file. Nothing is filed until someone approves." />
      <Seg label="Show" value={status} onChange={setStatus} options={[['waiting', `Waiting · ${waiting}`], ['all', 'All']] as const} />
      <div className="gap16" />
      {q.isLoading ? <SkelList rows={5} variant="conv" /> : (
        <div className="ph-grp" role="group" aria-label="Requests">
          <AnimatePresence initial={false}>
            {rows.map(a => {
              const Ico = ICON[a.source];
              return (
                <motion.button key={a.id} layout="position" transition={spring.gentle} exit={{ opacity: 0 }} type="button" className="ph-row" data-row={a.id} onClick={() => open(a.id)}>
                  <span className={`ph-ic ${IC[tone(a)]}`}><Ico /></span>
                  <span className="ph-rt"><div className="t">{a.title}</div><div className="s">{a.sourceLabel} · {whenOf(a.createdAt)} · {classify(a)}</div></span>
                  <Tag tone={TAG[a.status]}>{STATUS_WORD[a.status]}</Tag>
                </motion.button>
              );
            })}
          </AnimatePresence>
          {!rows.length && <div className="ph-rowx"><span className="ph-rt"><div className="t">All clear</div><div className="s">Nothing is waiting for you.</div></span></div>}
        </div>
      )}
      {groups.map(g => <div key={g.spaceId}><div className="gap16" /><Suggestions g={g} /></div>)}
      <ForwardHelp>{(addr, on) => <div className="ph-note">Forward any email to {addr}, or copy it in. The assistant proposes where it belongs; you approve it here.{on ? '' : ' Not connected on this server yet.'}</div>}</ForwardHelp>
      <AnimatePresence>{cur && <RequestSheet key={cur.id} a={cur} onClose={() => open(null)} />}</AnimatePresence>
    </div>
  );
}

function RequestSheet({ a, onClose }: { a: ProposedAction; onClose: () => void }) {
  const decide = useDecide();
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const from = typeof a.origin.from === 'string' ? a.origin.from : a.owner.id === me.data?.id ? 'you' : a.owner.name;
  const busy = decide.busy(a.id);
  const canUndo = a.status === 'ignored' && a.kind !== 'nudge_check';
  const toPlace = PLACE_FIRST.has(a.kind);
  const [placing, setPlacing] = useState(false);
  return (
    <PSheet label={a.title} onClose={onClose} card={{ id: a.id, kind: a.kind }}>
      <div className="ph-hr"><Tag tone={TAG[a.status]}>{a.status === 'waiting' ? 'Needs approval' : STATUS_WORD[a.status]}</Tag><span className="f13 mu">{a.kindLabel} · {whenOf(a.createdAt)} · from {from}</span></div>
      <h2>{a.title}</h2>
      {NOT_IGNORABLE.has(a.kind) ? <ApproveCard action={a} variant="detail" /> : <>
        <div className="ph-lbl2">What came in</div>
        <div className="ph-came">{cameIn(a)}</div>
        <div className="ph-lbl2">{toPlace ? 'Why it was not logged' : 'What the assistant will do'}</div>
        {isRetargetable(a) && <><EmailTarget a={a} /><EmailExtras a={a} phone /></>}
        {recordingMinutes(a) && <><div className="ph-lbl2">The minutes</div><div className="ph-grp" style={{ padding: 14 }}><MinutesBody m={recordingMinutes(a)!} /></div>{a.status === 'waiting' && <EmailExtras a={a} phone />}</>}
        <div className="ph-grp">{a.rows.map(r => <div className="ph-kv" key={r.label}><span>{r.label}</span><span>{r.value}</span></div>)}</div>
        {a.status === 'waiting' && toPlace ? <>
          {/* An email the assistant could not place: pick the space and track by hand, then it is a card to approve. */}
          {placing && <PlacePicker a={a} phone />}
          <div className="gap8" />
          {!placing && <button type="button" className="ph-btf" disabled={busy} onClick={() => setPlacing(true)}>Log it</button>}
          <button type="button" className="ph-btf sec" disabled={busy} onClick={() => decide.ignore(a)}>Dismiss</button>
        </> : a.status === 'waiting' ? <>
          <div className="gap8" />
          <button type="button" className="ph-btf" disabled={busy} onClick={() => decide.approve(a)}>Approve</button>
          <button type="button" className="ph-btf sec" disabled={busy} onClick={() => decide.ignore(a)}>Ignore</button>
        </> : <>
          <div className="ph-sres" role="status">{a.status === 'approved' && <P.check />}{a.status === 'approved' ? 'Approved. It is done.' : toPlace ? 'Dismissed. Nothing was logged.' : 'Ignored. Nothing was done.'}</div>
          {canUndo && <button type="button" className="ph-btf sec" onClick={() => { void api.actions.restore(a.id).then(() => { toast('Back in Waiting.'); void qc.invalidateQueries(); }, (e: unknown) => toast(e instanceof Error ? e.message : 'Could not undo.')); }}>Undo</button>}
        </>}
      </>}
      {a.conversationId && <Link className="ph-btf ghost" to={`/assistant/${a.conversationId}`}>Open the conversation</Link>}
    </PSheet>
  );
}

function Suggestions({ g }: { g: { spaceId: string; spaceName: string; cards: ProposedAction[] } }) {
  const [open, setOpen] = useState(false);
  const decide = useDecide();
  const cards = useLinger(g.cards, a => a.status !== 'waiting');
  const n = g.cards.filter(a => a.status === 'waiting').length;
  return (
    <div className="ph-pcg" data-group={g.spaceName}>
      <button type="button" className={`ph-pcgh${open ? ' open' : ''}`} onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="aiorb" /><span className="ph-rt"><div className="t">Context suggestions</div><div className="s">{g.spaceName} · optional</div></span>
        <span className="ph-cgn" aria-label={`${n} waiting`}>{n}</span><P.chev />
      </button>
      {open && (
        <div className="ph-pcgb">
          {cards.map(a => (
            <div className="ph-pcgc" key={a.id} data-card={a.id} data-kind={a.kind}>
              <div className="src">{a.sourceLabel}</div>
              <b>{a.title}</b>
              <div className="m">{a.rows.find(r => r.label === 'Context')?.value ?? a.spaceName}</div>
              <div className="q">{a.rows.find(r => r.label === 'Adds' || r.label === 'Replaces it with')?.value}</div>
              {a.status === 'waiting'
                ? <div className="b"><button type="button" className="ph-btm pri" disabled={decide.busy(a.id)} onClick={() => decide.approve(a)}>Approve</button><button type="button" className="ph-btm dis" disabled={decide.busy(a.id)} onClick={() => decide.ignore(a)}>Ignore</button></div>
                : <span className={`ph-cgr${a.status === 'approved' ? '' : ' ig'}`} role="status">{a.status === 'approved' ? 'Added to the context' : 'Ignored'}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
